/**
 * Vessel Configuration Mapping for Familiarisation System
 * Maps VesselSettingsModal fields to familiarisation conditional logic
 */

/**
 * Transform vessel configuration from VesselSettingsModal format
 * to format expected by familiarisation system
 * 
 * @param {Object} vesselConfig - Raw vessel configuration from VesselSettingsModal
 * @returns {Object} Transformed configuration for familiarisation logic
 */
export function transformVesselConfig(vesselConfig) {
  if (!vesselConfig) return {};
  
  return {
    // Basic vessel info (direct mapping)
    vesselName: vesselConfig.vesselName,
    vesselType: vesselConfig.vesselType,
    category: vesselConfig.category,
    lengthOverall: parseFloat(vesselConfig.lengthOverall) || 0,
    
    // Equipment flags from VesselSettingsModal
    hasLiferaft: vesselConfig.hasLiferaft || vesselConfig.liferafts_fitted,
    hasEPIRB: vesselConfig.hasEPIRB,
    hasSART: vesselConfig.hasSART || vesselConfig.extra_equipment?.includes('sart'),
    hasGrabBag: vesselConfig.hasGrabBag || vesselConfig.equipment?.includes('grab_bag'),
    hasAccommodation: vesselConfig.hasAccommodation || vesselConfig.cabin_accommodation,
    hasGalley: vesselConfig.hasGalley,
    hasEnclosedSpaces: vesselConfig.hasEnclosedSpaces || vesselConfig.enclosed_accommodation_present,
    hasEmergencySteering: vesselConfig.hasEmergencySteering,
    
    // Equipment arrays
    equipment: vesselConfig.equipment || [],
    extra_equipment: vesselConfig.extra_equipment || [],
    
    // Operations array
    operations: vesselConfig.operations || [],
    
    // Additional flags
    internationalVoyages: vesselConfig.internationalVoyages,
    
    // Pass through any other properties
    ...vesselConfig
  };
}

/**
 * Vessel configuration field definitions
 * Maps UI fields to familiarisation conditions
 */
export const VESSEL_CONFIG_FIELDS = {
  // Length-based conditions
  lengthTriggers: {
    liferaftRequired: 12, // Vessels >12m require liferaft familiarisation
    epribRequired: 12    // Vessels >12m require EPIRB familiarisation
  },
  
  // Equipment checkboxes that trigger familiarisation items
  equipmentTriggers: {
    // From equipment array
    'fixed_firefighting': {
      familiarisationItems: ['fixed_fire_operation', 'fixed_fire_isolation'],
      description: 'Fixed fire suppression systems (CO2, foam, etc.)'
    },
    'gas_detection': {
      familiarisationItems: ['gas_alarm_meanings', 'gas_testing'],
      description: 'Fixed gas detection systems'
    },
    'winch_lifting_gear': {
      familiarisationItems: ['crane_limits', 'lifting_signals', 'exclusion_zones', 'lifting_gear_inspection'],
      description: 'Cranes, winches, or lifting equipment'
    },
    'grab_bag': {
      familiarisationItems: ['grab_bag_contents'],
      description: 'Emergency grab bag'
    }
  },
  
  // Extra equipment checkboxes
  extraEquipmentTriggers: {
    'sart': {
      familiarisationItems: ['sart_operation'],
      description: 'Search and Rescue Transponder'
    },
    'immersion_suits': {
      familiarisationItems: ['immersion_suits'],
      description: 'Immersion/survival suits'
    }
  },
  
  // Operations checkboxes that trigger familiarisation items
  operationsTriggers: {
    'towing_operations': {
      familiarisationItems: ['tow_connection', 'girting_prevention', 'tow_communications', 'towing_risk_assessments'],
      description: 'Towing operations'
    },
    'passenger_operations': {
      familiarisationItems: ['passenger_areas', 'passenger_manifest', 'passenger_safety_announcement', 
                           'passenger_briefing', 'passenger_counting', 'crowd_control'],
      description: 'Passenger carrying operations'
    },
    'dangerous_goods': {
      familiarisationItems: ['dg_segregation', 'dg_emergency', 'dg_documentation', 'dg_ppe'],
      description: 'Dangerous goods carriage'
    },
    'dive_support': {
      familiarisationItems: ['dive_emergency', 'dive_communications', 'dive_exclusion'],
      description: 'Dive support operations'
    },
    'survey_operations': {
      familiarisationItems: [], // No specific items but could add survey-specific ones
      description: 'Survey and research operations'
    }
  },
  
  // Space-based triggers
  spaceTriggers: {
    'hasEnclosedSpaces': {
      familiarisationItems: ['enclosed_entry', 'rescue_equipment', 'enclosed_space_risks'],
      description: 'Enclosed or confined spaces'
    },
    'hasGalley': {
      familiarisationItems: ['galley_isolation', 'grease_fire', 'galley_hygiene'],
      description: 'Galley/cooking facilities'
    },
    'hasAccommodation': {
      familiarisationItems: ['accommodation_safety', 'co_detectors'],
      description: 'Crew accommodation spaces'
    }
  },
  
  // Category-based triggers
  categoryTriggers: {
    offshore: ['0', '1', '2'], // Categories that require pyrotechnics
    inshore: ['3', '4', '5', '6']
  }
};

/**
 * Get a summary of what familiarisation items will be triggered
 * by the current vessel configuration
 * 
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Summary of triggered items by category
 */
export function getFamiliarisationSummary(vesselConfig) {
  const config = transformVesselConfig(vesselConfig);
  const summary = {
    lengthBased: [],
    equipmentBased: [],
    operationsBased: [],
    spaceBased: [],
    categoryBased: []
  };
  
  // Check length triggers
  if (config.lengthOverall > 12) {
    summary.lengthBased.push({
      trigger: 'Vessel >12m',
      items: ['Liferaft procedures', 'EPIRB activation', 'Hydrostatic releases']
    });
  }
  
  // Check equipment triggers
  if (config.equipment) {
    config.equipment.forEach(equip => {
      if (VESSEL_CONFIG_FIELDS.equipmentTriggers[equip]) {
        summary.equipmentBased.push({
          trigger: VESSEL_CONFIG_FIELDS.equipmentTriggers[equip].description,
          items: VESSEL_CONFIG_FIELDS.equipmentTriggers[equip].familiarisationItems
        });
      }
    });
  }
  
  // Check extra equipment
  if (config.extra_equipment) {
    config.extra_equipment.forEach(equip => {
      if (VESSEL_CONFIG_FIELDS.extraEquipmentTriggers[equip]) {
        summary.equipmentBased.push({
          trigger: VESSEL_CONFIG_FIELDS.extraEquipmentTriggers[equip].description,
          items: VESSEL_CONFIG_FIELDS.extraEquipmentTriggers[equip].familiarisationItems
        });
      }
    });
  }
  
  // Check operations
  if (config.operations) {
    config.operations.forEach(op => {
      if (VESSEL_CONFIG_FIELDS.operationsTriggers[op]) {
        summary.operationsBased.push({
          trigger: VESSEL_CONFIG_FIELDS.operationsTriggers[op].description,
          items: VESSEL_CONFIG_FIELDS.operationsTriggers[op].familiarisationItems
        });
      }
    });
  }
  
  // Check spaces
  Object.entries(VESSEL_CONFIG_FIELDS.spaceTriggers).forEach(([key, value]) => {
    if (config[key]) {
      summary.spaceBased.push({
        trigger: value.description,
        items: value.familiarisationItems
      });
    }
  });
  
  // Check category
  if (VESSEL_CONFIG_FIELDS.categoryTriggers.offshore.includes(config.category)) {
    summary.categoryBased.push({
      trigger: `Category ${config.category} (offshore)`,
      items: ['Pyrotechnic flares and distress signals']
    });
  }
  
  return summary;
}

/**
 * Validate vessel configuration has minimum required fields
 * for familiarisation system
 * 
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Validation result with any missing fields
 */
export function validateVesselConfig(vesselConfig) {
  const errors = [];
  const warnings = [];
  
  // Required fields
  if (!vesselConfig.vesselName) {
    errors.push('Vessel name is required');
  }
  
  if (!vesselConfig.vesselType) {
    errors.push('Vessel type is required');
  }
  
  if (!vesselConfig.category) {
    errors.push('Vessel category is required');
  }
  
  if (!vesselConfig.lengthOverall) {
    warnings.push('Vessel length not specified - some familiarisation items may be missing');
  }
  
  // Check if any equipment is configured
  const hasAnyEquipment = 
    vesselConfig.equipment?.length > 0 ||
    vesselConfig.extra_equipment?.length > 0 ||
    vesselConfig.hasLiferaft ||
    vesselConfig.hasEPIRB ||
    vesselConfig.hasSART;
  
  if (!hasAnyEquipment) {
    warnings.push('No equipment configured - familiarisation will only include basic items');
  }
  
  // Check if any operations are configured
  if (!vesselConfig.operations || vesselConfig.operations.length === 0) {
    warnings.push('No operations specified - operation-specific familiarisation items will not be included');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}